import type { ISimpleRESTServiceRunner } from "@envestnet/envjs-component-openenv/distribution";
import { Applications } from "types/Dashboard";
import { PasswordPolicy } from "types/PasswordPolicy";

import {
  ChallengeQuestion,
  ChallengeQuestionSubmissionPayload,
  ChallengeQuestionSubmissionResponsePayload,
} from "./DTO/ChallengeQuestionDTO";
import {
  CheckUpdatedTnC,
  UpdatedTnCAcceptanceResponse,
} from "./DTO/CheckUpdatedTermsDTO";
import { MFAMethods, UserInfo } from "./DTO/MFAmethodsDTO";

const getChallengeQuestion =
  (runner: ISimpleRESTServiceRunner) =>
  async (activationCode: string): Promise<ChallengeQuestion> => {
    const url = `/ciam/ext/activation/challengequestion?activationCode=${activationCode}&client_id=${window.config.APIGEE_CLIENT_ID}`;

    return runner.performGET(url);
  };

const getSecurityMethods =
  (runner: ISimpleRESTServiceRunner) =>
  async (activationCode: string): Promise<MFAMethods> => {
    const url = `/ciam/ext/activation/authenticators?activationCode=${activationCode}`;

    return runner.performGET(url);
  };

const putMFAEnroll =
  (runner: ISimpleRESTServiceRunner) =>
  async (type: string, activationCode: string): Promise<MFAMethods> => {
    const url = `/ciam/ext/activation/authenticators/${type}?activationCode=${activationCode}`;

    return runner.performPUT(url, { factorType: "TOTP" });
  };

const putUpdateMFA =
  (runner: ISimpleRESTServiceRunner) =>
  async (type: string, cidpId: string, payload: any): Promise<MFAMethods> => {
    const url = `/ciam/ext/users/${cidpId}/authenticators/${type}`;
    return runner.performPUT(url, payload);
  };

const updatePassCode =
  (runner: ISimpleRESTServiceRunner) =>
  async (type: string, cidpId: string, passcode: string): Promise<any> =>
    runner.performPOST(
      `/ciam/ext/users/${cidpId}/authenticators/${type}/passcode`,
      { passcode }
    );

const submitChallengeAnswer =
  (runner: ISimpleRESTServiceRunner) =>
  async (
    activationCode: string,
    payload: ChallengeQuestionSubmissionPayload
  ): Promise<ChallengeQuestionSubmissionResponsePayload> =>
    runner
      .useRequestHeaders((requestHeaders) => {
        requestHeaders.set("Content-Type", "application/json");
      })
      .performPOST(
        `/ciam/ext/activation/token?activationCode=${activationCode}&client_id=${window.config.APIGEE_CLIENT_ID}`,
        payload
      );

const loadPasswordPolicies =
  (runner: ISimpleRESTServiceRunner) =>
  async (activationCode: string): Promise<PasswordPolicy> =>
    runner.performGET(
      `/ciam/ext/activation/authenticators/password/policy?activationCode=${activationCode}`
    );

const getAuthenticatedPasswordPolicies =
  (runner: ISimpleRESTServiceRunner) =>
  async (cidpId: string, accessToken: string): Promise<CheckUpdatedTnC> => {
    const url = `/ciam/ext/users/${cidpId}/passwordpolicy`;

    return runner
      .useRequestHeaders((requestheaders) =>
        requestheaders.set("Authorization", `Bearer ${accessToken}`)
      )
      .performGET(url);
  };

const submitUpdatePassword =
  (runner: ISimpleRESTServiceRunner) =>
  async (
    cidpId: string,
    accessToken: string,
    payload: { oldPassword: string; newPassword: string }
  ): Promise<any> =>
    runner
      .useRequestHeaders((requestHeaders) =>
        requestHeaders.set("Content-Type", "application/json")
      )
      .useRequestHeaders((requestheaders) =>
        requestheaders.set("Authorization", `Bearer ${accessToken}`)
      )
      .performPUT(`/ciam/ext/users/${cidpId}/changepassword`, payload);

const validateDashboardUsername =
  (runner: ISimpleRESTServiceRunner) =>
  async (
    cidpId: string,
    accessToken: string,
    payload: { username: string }
  ): Promise<any> =>
    runner
      .useRequestHeaders((requestHeaders) =>
        requestHeaders.set("Content-Type", "application/json")
      )
      .useRequestHeaders((requestHeaders) => {
        if (accessToken) {
          requestHeaders.set("Authorization", `Bearer ${accessToken}`);
        }
      })
      .performPOST(`/ciam/ext/users/${cidpId}/username/_validate`, payload);

const validateActivationUsername =
  (runner: ISimpleRESTServiceRunner) =>
  async (activationCode: string, payload: { username: string }): Promise<any> =>
    runner
      .useRequestHeaders((requestHeaders) =>
        requestHeaders.set("Content-Type", "application/json")
      )
      .performPOST(
        `/ciam/ext/activation/username/_validate?activationCode=${activationCode}`,
        payload
      );

const updateUsername =
  (runner: ISimpleRESTServiceRunner) =>
  async (cidpId: string, payload: { username: string }): Promise<any> =>
    runner
      .useRequestHeaders((requestHeaders) =>
        requestHeaders.set("Content-Type", "application/json")
      )
      .performPUT(`/ciam/ext/users/${cidpId}/username`, payload);

const submitPhoneNumber =
  (runner: ISimpleRESTServiceRunner) =>
  async (
    activationCode: string,
    payload: { phoneNumber: string }
  ): Promise<any> =>
    runner
      .useRequestHeaders((requestHeaders) =>
        requestHeaders.set("Content-Type", "application/json")
      )
      .performPUT(
        `/ciam/ext/activation/authenticators/phone?activationCode=${activationCode}`,
        payload
      );

const submitPhoneVerificationCode =
  (runner: ISimpleRESTServiceRunner) =>
  async (activationCode: string, passcode: string): Promise<any> =>
    runner
      .useRequestHeaders((requestHeaders) =>
        requestHeaders.set("Content-Type", "application/json")
      )
      .performPOST(
        `/ciam/ext/activation/authenticators/phone/passcode?activationCode=${activationCode}`,
        { passcode }
      );

const getPasswordValidator =
  (runner: ISimpleRESTServiceRunner) =>
  async (activationCode: string): Promise<ChallengeQuestion> => {
    const url = `/ciam/ext/activation/PasswordRequestDTO?activationCode=${activationCode}`;

    return runner.performGET(url);
  };

const getUserInfo =
  (runner: ISimpleRESTServiceRunner) =>
  async (activationCode: string): Promise<UserInfo> => {
    const url = `/ciam/ext/activation/userinfo?activationCode=${activationCode}`;

    return runner.performGET(url);
  };

const submitPasswordActivation =
  (runner: ISimpleRESTServiceRunner) =>
  async (activationCode: string, payload: any): Promise<any> =>
    runner
      .useRequestHeaders((requestHeaders) =>
        requestHeaders.set("Content-Type", "application/json")
      )
      .performPUT(
        `/ciam/ext/activation/authenticators/password/policy?activationCode=${activationCode}`,
        payload
      );

const submitAuthenticatorPassCode =
  (runner: ISimpleRESTServiceRunner) =>
  async (
    type: string,
    activationCode: string,
    passcode: string
  ): Promise<any> =>
    runner
      // .useRequestHeaders((requestHeaders) =>
      //   requestHeaders.set("Content-Type", "application/json")
      // )
      .performPOST(
        `/ciam/ext/activation/authenticators/${type}/passcode?activationCode=${activationCode}`,
        { passcode }
      );

const getAuthenticatorStatus =
  (runner: ISimpleRESTServiceRunner) =>
  async (type: string, activationCode: string): Promise<ChallengeQuestion> => {
    const url = `/ciam/ext/activation/authenticators/${type}/status?activationCode=${activationCode}`;
    return runner.performGET(url);
  };

const getUpdateMfaStatus =
  (runner: ISimpleRESTServiceRunner) =>
  async (type: string, cidpId: string): Promise<ChallengeQuestion> => {
    const url = `/ciam/ext/users/${cidpId}/authenticators/${type}/status`;
    return runner.performGET(url);
  };

const setTermsAndConditionStatus =
  (runner: ISimpleRESTServiceRunner) =>
  async (activationCode: string, data: any): Promise<ChallengeQuestion> => {
    const url = `/ciam/ext/activation/terms/accept?activationCode=${activationCode}`;
    return runner.performPUT(url, { ...data });
  };

const getTermsAndCondition =
  (runner: ISimpleRESTServiceRunner) =>
  async (activationCode: string): Promise<any> =>
    runner.performGET(
      `/ciam/ext/activation/terms/content?activationCode=${activationCode}`
    );

const checkUserAcceptedLatestTerms =
  (runner: ISimpleRESTServiceRunner) =>
  async (cidpId: string): Promise<CheckUpdatedTnC> => {
    const url = `/ciam/ext/users/${cidpId}/terms/status`;

    return runner.performGET(url);
  };

const getUpdatedTnC =
  (runner: ISimpleRESTServiceRunner) =>
  async (cidpId: string): Promise<string> => {
    const url = `/ciam/ext/users/${cidpId}/terms/content`;

    return runner.performGET(url);
  };

const setAcceptanceForUpdatedTnC =
  (runner: ISimpleRESTServiceRunner) =>
  async (cidpId: string, data: any): Promise<UpdatedTnCAcceptanceResponse> => {
    const url = `/ciam/ext/users/${cidpId}/terms/accept`;
    return runner.performPUT(url, { ...data });
  };

const getAuthenticatorList =
  (runner: ISimpleRESTServiceRunner) =>
  async (cidpId: string): Promise<any> =>
    runner.performGET(`/ciam/ext/users/${cidpId}/authenticators`);

const deleteMfaAuthenticator =
  (runner: ISimpleRESTServiceRunner) =>
  async (type: string, cidpId: string): Promise<any> =>
    runner.performDELETE(`/ciam/ext/users/${cidpId}/authenticators/${type}`);

const dashboardUserInfo =
  (runner: ISimpleRESTServiceRunner) => async (token: string) => {
    const url = "/oauth2/v2/userinfo";
    return runner
      .useRequestHeaders((requestHeaders) => {
        requestHeaders.set("Authorization", `Bearer ${token}`);
      })
      .performGET(url);
  };

const userAuthentication =
  (runner: ISimpleRESTServiceRunner) =>
  async (cidpId: string, password: string): Promise<string> => {
    const url = `/ciam/ext/users/${cidpId}/authn`;

    return runner.performPOST(url, { password });
  };

const getUserAuthenticators =
  (runner: ISimpleRESTServiceRunner) =>
  async (cidpId: string): Promise<CheckUpdatedTnC> => {
    const url = `/ciam/ext/users/${cidpId}/authenticators`;

    return runner.performGET(url);
  };

const getUserApplications =
  (runner: ISimpleRESTServiceRunner) =>
  async (cidpId: string): Promise<Array<Applications>> => {
    const url = `/ciam/ext/users/${cidpId}/applications`;

    return runner.performGET(url);
  };

const getPasskeys =
  (runner: ISimpleRESTServiceRunner) =>
  async (cidpId: string): Promise<any> => {
    const url = `/ciam/ext/users/${cidpId}/credentials`;

    return runner.performGET(url);
  };

const deletePasskey =
  (runner: ISimpleRESTServiceRunner) =>
  async (credentialId: string, cidpId: string): Promise<any> =>
    runner.performDELETE(
      `/ciam/ext/users/${cidpId}/credentials/${credentialId}`
    );

const AuthenticationService = {
  getChallengeQuestion,
  submitChallengeAnswer,
  submitPhoneNumber,
  submitPhoneVerificationCode,
  getPasswordValidator,
  submitPasswordActivation,
  getSecurityMethods,
  putMFAEnroll,
  submitAuthenticatorPassCode,
  getAuthenticatorStatus,
  getUpdateMfaStatus,
  getUserInfo,
  setTermsAndConditionStatus,
  loadPasswordPolicies,
  getTermsAndCondition,
  checkUserAcceptedLatestTerms,
  getUpdatedTnC,
  setAcceptanceForUpdatedTnC,
  getAuthenticatorList,
  deleteMfaAuthenticator,
  dashboardUserInfo,
  getAuthenticatedPasswordPolicies,
  submitUpdatePassword,
  validateDashboardUsername,
  validateActivationUsername,
  updateUsername,
  userAuthentication,
  getUserAuthenticators,
  putUpdateMFA,
  updatePassCode,
  getUserApplications,
  getPasskeys,
  deletePasskey,
};

type IAuthenticationService = typeof AuthenticationService;
export type AuthenticationServiceInnerMethods = {
  [key in keyof IAuthenticationService]: ReturnType<
    IAuthenticationService[key]
  >;
};

export default AuthenticationService;
